import posthog from 'posthog-js';

type Surface = 'video_modal' | 'explore' | 'profile' | 'feed' | 'other';

type EndReason =
  | 'impression_ended'
  | 'clip_changed'
  | 'modal_closed'
  | 'unmount';

export type ClipViewSession = {
  readonly viewId: string;
  tick: (currentSec: number, durationSec: number) => void;
  markCompleted: () => void;
  end: (reason?: EndReason) => void;
};

const MILESTONES = [0.25, 0.5, 0.75] as const;

function newViewId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function startClipView(params: {
  clipId: string;
  surface: Surface;
  durationSec?: number;
}): ClipViewSession {
  const viewId = newViewId();
  const startedAtMs = Date.now();

  posthog.capture('clip_view_started', {
    clip_id: params.clipId,
    view_id: viewId,
    surface: params.surface,
    clip_duration_s: params.durationSec ?? null,
  });

  let maxPositionSec = 0;
  let knownDurationSec = params.durationSec ?? 0;
  const milestonesFired = new Set<number>();
  let completed = false;
  let ended = false;

  return {
    viewId,
    tick(currentSec, durationSec) {
      if (ended) return;
      if (Number.isFinite(durationSec) && durationSec > knownDurationSec) {
        knownDurationSec = durationSec;
      }
      if (currentSec > maxPositionSec) maxPositionSec = currentSec;
      if (!knownDurationSec) return;
      const ratio = currentSec / knownDurationSec;
      for (const m of MILESTONES) {
        if (ratio >= m && !milestonesFired.has(m)) {
          milestonesFired.add(m);
          posthog.capture('clip_view_progress', {
            clip_id: params.clipId,
            view_id: viewId,
            surface: params.surface,
            milestone: m,
          });
        }
      }
    },
    markCompleted() {
      completed = true;
    },
    end(reason = 'impression_ended') {
      if (ended) return;
      ended = true;
      const durationWatchedMs = Date.now() - startedAtMs;
      const percentWatched = knownDurationSec
        ? Math.min(1, maxPositionSec / knownDurationSec)
        : null;
      const wasCompleted =
        completed || (percentWatched !== null && percentWatched >= 0.9);
      posthog.capture('clip_view_ended', {
        clip_id: params.clipId,
        view_id: viewId,
        surface: params.surface,
        clip_duration_s: knownDurationSec || null,
        duration_watched_ms: durationWatchedMs,
        max_position_s: maxPositionSec,
        percent_watched: percentWatched,
        completed: wasCompleted,
        reason,
      });
    },
  };
}
